import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteDraft } from '../services/api';

/**
 * Card showing a summary of a saved draft.
 * Links to the draft board (read-only when completed).
 * @param {Object} draft
 * @param {Function} [onDelete] - Called with draftId after successful deletion
 */
function DraftListItem({ draft, onDelete }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const date = draft.createdAt
    ? new Date(draft.createdAt).toLocaleDateString()
    : '—';
  const isCompleted = draft.status === 'completed';

  function handleCardClick() {
    if (!confirming) {
      navigate(`/draft/${draft.draftId}`);
    }
  }

  function handleDeleteClick(e) {
    e.stopPropagation();
    setConfirming(true);
  }

  function handleCancelDelete(e) {
    e.stopPropagation();
    setConfirming(false);
  }

  async function handleConfirmDelete(e) {
    e.stopPropagation();
    setDeleting(true);
    try {
      await deleteDraft(draft.draftId);
      onDelete?.(draft.draftId);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <div
      className='draft-list-item'
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
    >
      <div className='draft-list-item-header'>
        <span className='draft-list-item-name'>{draft.draftName || 'Untitled Draft'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {draft.mockDraft && (
            <span className='draft-status-badge mock'>Mock Draft</span>
          )}
          <span className={`draft-status-badge ${isCompleted ? 'completed' : 'active'}`}>
            {isCompleted ? 'Completed' : 'Active'}
          </span>
          {onDelete && !confirming && (
            <button
              className='filter-pill'
              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
              onClick={handleDeleteClick}
              title='Delete draft'
            >
              ×
            </button>
          )}
          {confirming && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Delete?</span>
              <button
                className='filter-pill active'
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? '…' : 'Yes'}
              </button>
              <button
                className='filter-pill'
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                No
              </button>
            </span>
          )}
        </div>
      </div>
      <div className='draft-list-item-meta'>
        <span>{(draft.sport || 'NFL').toUpperCase()}</span>
        <span>{draft.teamCount} teams</span>
        <span>{draft.rounds} rounds</span>
        <span>{date}</span>
      </div>
    </div>
  );
}

export default DraftListItem;